import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, StatCard, SectionLabel, ProgressBar, Badge, Avatar, Timeline, ActivityItem } from "@realtyflows/ui";
import { getClients, getClientStats, getWeeklyPerformance, getActivity, getDealStats, getMyTestimonial, submitTestimonial } from "../api";
import ScoreBadge from "../components/ScoreBadge";
import Alert from "../components/Alert";
import MiniBarChart from "../components/MiniBarChart";
import useResponsive from "../hooks/useResponsive";

export default function Dashboard() {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const [stats, setStats] = useState({ total: 0, replied: 0, showings: 0, closed: 0, sources: [], engagement_patterns: [] });
  const [, setDealStats] = useState({ total: 0, closed: 0, showings: 0 });
  const [weeklyData, setWeeklyData] = useState([]);
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ quote: "", rating: 5 });
  const [feedbackStatus, setFeedbackStatus] = useState(null); // null | 'submitted' | 'existing'
  const [showFeedback, setShowFeedback] = useState(() => localStorage.getItem("rf_dismiss_feedback") !== "1");
  const [editingFeedback, setEditingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  useEffect(() => {
    Promise.all([getClientStats(), getClients(), getActivity(6), getDealStats(), getWeeklyPerformance()])
      .then(([s, l, a, ds, wd]) => { setStats(s); setClients(l); setActivities(a); setDealStats(ds); setWeeklyData(wd || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
    getMyTestimonial().then(t => {
      if (t) { setFeedback({ quote: t.quote, rating: t.rating }); setFeedbackStatus("existing"); }
    }).catch(() => {});
  }, []);

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading dashboard...</div>;

  const activityColors = {
    "New client captured": t.emerald, "AI sent email": t.amber, "Showing scheduled": t.emerald,
    "Offer submitted": t.coral, "Deal closed": t.emeraldDeep, "Client score updated": t.purple,
  };

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Clients" value={stats.total.toString()} trend="+12%" delay="0s" />
        <StatCard label="Replied" value={stats.replied.toString()} trend="+8%" delay="0.07s" />
        <StatCard label="Showings" value={stats.showings.toString()} trend="+23%" delay="0.14s" />
        <StatCard label="Deals Closed" value={stats.closed.toString()} trend="+50%" delay="0.21s" />
      </div>

      {/* Funnel + Chart */}
      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "1fr" : "1fr 1.4fr", gap: 16, marginBottom: 24 }}>
        <Panel padding={20}>
          <SectionLabel>Conversion Funnel</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "Clients Captured", value: stats.total > 0 ? 100 : 0, color: t.emerald },
              { label: "Replied", value: stats.total > 0 ? Math.round((stats.replied / stats.total) * 100) : 0, color: t.amber },
              { label: "Showings", value: stats.total > 0 ? Math.round((stats.showings / stats.total) * 100) : 0, color: t.sky },
              { label: "Offers", value: stats.total > 0 ? Math.round(((stats.offers || 0) / stats.total) * 100) : 0, color: t.coral },
              { label: "Closed", value: stats.total > 0 ? Math.round((stats.closed / stats.total) * 100) : 0, color: t.emeraldDeep },
            ].map((f, i) => (
              <ProgressBar key={i} value={f.value} color={f.color} size="sm" label={f.label} showPercent />
            ))}
          </div>
        </Panel>

        <Panel padding={20}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Weekly Performance</div>
            <div style={{ display: "flex", gap: 12 }}>
              {[["Clients", `${t.emerald}20`], ["Showings", t.emerald], ["Deals", t.emeraldDeep]].map(([l, c]) => (
                <div key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
                  <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{l}</span>
                </div>
              ))}
            </div>
          </div>
          <MiniBarChart data={weeklyData} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 16 }}>
            {[
              { label: "Avg. Response", value: "4 min", sub: "AI automated" },
              { label: "Reply Rate", value: stats.total > 0 ? Math.round((stats.replied / stats.total) * 100) + "%" : "0%", sub: "Up from 48%" },
              { label: "Close Rate", value: stats.total > 0 ? Math.round((stats.closed / stats.total) * 100) + "%" : "0%", sub: "Industry: 3.2%" },
            ].map((s, i) => (
              <div key={i} style={{ background: t.surfaceWarm, borderRadius: 8, padding: "10px 12px", border: `1px solid ${t.border}` }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, fontFamily: font.mono }}>{s.value}</div>
                <div style={{ fontSize: 10, color: t.textPrimary, marginTop: 1, fontFamily: font.body }}>{s.label}</div>
                <div style={{ fontSize: 9, color: t.emerald, marginTop: 1, fontFamily: font.body }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Client Sources + Recent Activity */}
      <div style={{ display: "grid", gridTemplateColumns: responsive.settingsColumns, gap: 16, marginBottom: 24 }}>
        <Panel padding={20}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Client Sources</div>
          {(stats.sources || []).map(s => {
            const srcColors = { Zillow: t.emerald, "Facebook Ad": t.sky, Website: t.emeraldDeep, Referral: t.amber, Instagram: t.coral };
            const pct = stats.total > 0 ? Math.round((parseInt(s.count) / stats.total) * 100) : 0;
            return (
              <div key={s.source} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: (srcColors[s.source] || t.emerald) + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: srcColors[s.source] || t.emerald }}>
                  {s.source[0]}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{s.source}</span>
                    <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>{s.count} clients</span>
                  </div>
                  <div style={{ height: 4, background: t.border, borderRadius: 99 }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: srcColors[s.source] || t.emerald, borderRadius: 99 }} />
                  </div>
                </div>
              </div>
            );
          })}
        </Panel>

        <Panel padding={20}>
          <SectionLabel>Recent Activity</SectionLabel>
          <Timeline>
            {activities.map((a) => {
              const iconMap = { "AI sent email": "", "Showing scheduled": "", "Offer submitted": "", "Deal closed": "", "Client score updated": "" };
              return (
                <ActivityItem
                  key={a.id}
                  icon={iconMap[a.action] || "\u25CE"}
                  color={activityColors[a.action] || t.emerald}
                  title={a.action}
                  subtitle={a.detail}
                  timestamp={new Date(a.created_at).toLocaleDateString()}
                />
              );
            })}
          </Timeline>
        </Panel>
      </div>

      {/* Engagement Patterns */}
      {(stats.engagement_patterns || []).length > 0 && (
        <Panel padding={20} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Client Engagement Patterns</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            {(() => {
              const patternLabels = {
                active: "Active", ghost_post_showing: "Ghost (Post-Showing)", ghost_pre_showing: "No-Show",
                ghost_post_offer: "Ghost (Post-Offer)", seasonal_pauser: "Seasonal", slow_burner: "Slow Burner", referred_out: "Referred Out",
              };
              const patternColors2 = {
                active: t.emerald, ghost_post_showing: t.coral, ghost_pre_showing: t.coral,
                ghost_post_offer: "#e74c3c", seasonal_pauser: t.amber, slow_burner: t.sky, referred_out: t.textMuted,
              };
              return (stats.engagement_patterns || []).map(p => (
                <div key={p.engagement_pattern} style={{
                  background: (patternColors2[p.engagement_pattern] || t.emerald) + "10",
                  border: `1px solid ${(patternColors2[p.engagement_pattern] || t.emerald)}25`,
                  borderRadius: 10, padding: "12px 14px", textAlign: "center",
                }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: patternColors2[p.engagement_pattern] || t.emerald, fontFamily: font.mono }}>
                    {parseInt(p.count)}
                  </div>
                  <div style={{ fontSize: 10, color: t.textSecondary, fontWeight: 600, fontFamily: font.body, marginTop: 2 }}>
                    {patternLabels[p.engagement_pattern] || p.engagement_pattern || "Unknown"}
                  </div>
                </div>
              ));
            })()}
          </div>
        </Panel>
      )}

      {/* Recent Clients */}
      <Panel padding={20}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Recent Clients</div>
          <div style={{ fontSize: 12, color: t.emerald, cursor: "pointer", fontWeight: 600, fontFamily: font.body }} onClick={() => navigate("/clients")}>View all &rarr;</div>
        </div>
        {clients.slice(0, 5).map(client => (
          <div key={client.id} onClick={() => navigate(`/clients/detail?clientId=${client.id}`)}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 8px", borderRadius: 8, marginBottom: 4, cursor: "pointer", transition: "background 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <Avatar name={client.name} size={32} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{client.name}</div>
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>{client.source} &middot; {client.location}</div>
            </div>
            <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.mono }}>{client.budget}</div>
            <ScoreBadge score={client.score} />
            <Badge variant={client.status === "Closed" ? "emerald" : client.status === "Contacted" ? "emerald" : client.status === "Showing Scheduled" ? "amber" : client.status === "Offer Submitted" ? "purple" : "sky"}>{client.status}</Badge>
          </div>
        ))}
      </Panel>

      {/* Feedback Prompt */}
      {showFeedback && (
        <Panel padding={20} style={{ marginTop: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              {!feedbackStatus ? "How's your experience?" : "Your Feedback"}
            </div>
            <span style={{ fontSize: 11, color: t.textMuted, cursor: "pointer", fontFamily: font.body }} onClick={() => { setShowFeedback(false); localStorage.setItem("rf_dismiss_feedback", "1"); }}>Dismiss</span>
          </div>

          {(feedbackStatus === "submitted" || feedbackStatus === "existing") && !editingFeedback ? (
            <>
              {/* Display submitted feedback */}
              <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                {[1,2,3,4,5].map(s => (
                  <span key={s} style={{ fontSize: 18, color: s <= feedback.rating ? t.gold || "#C5A572" : t.border }}>{"\u2605"}</span>
                ))}
              </div>
              <p style={{
                fontSize: 13, color: t.textPrimary, fontFamily: font.body, fontStyle: "italic",
                lineHeight: 1.6, margin: "0 0 12px 0", padding: "12px 16px",
                background: t.surfaceWarm, borderRadius: 8, border: `1px solid ${t.border}`,
              }}>
                "{feedback.quote}"
              </p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 11, color: t.emerald || "#00a878", fontFamily: font.body, fontWeight: 600 }}>
                  Submitted {feedbackStatus === "submitted" ? "just now" : ""}
                </div>
                <button
                  onClick={() => setEditingFeedback(true)}
                  style={{
                    padding: "6px 16px", borderRadius: 6, background: "transparent",
                    border: `1px solid ${t.border}`, color: t.textMuted,
                    fontSize: 11, fontWeight: 600, fontFamily: font.body, cursor: "pointer",
                  }}
                >
                  Edit
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Edit / New feedback form */}
              <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
                {[1,2,3,4,5].map(s => (
                  <span key={s} onClick={() => setFeedback(f => ({ ...f, rating: s }))}
                    style={{ cursor: "pointer", fontSize: 22, color: s <= feedback.rating ? t.gold || "#C5A572" : t.border, transition: "color 0.15s" }}>
                    {"\u2605"}
                  </span>
                ))}
              </div>
              <textarea
                value={feedback.quote}
                onChange={e => setFeedback(f => ({ ...f, quote: e.target.value }))}
                placeholder="Tell us what you think about RealtyFlows..."
                style={{
                  width: "100%", minHeight: 80, padding: 12, borderRadius: 8,
                  border: `1px solid ${t.border}`, background: t.surfaceWarm,
                  fontFamily: font.body, fontSize: 13, color: t.textPrimary,
                  resize: "vertical", outline: "none", boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                {editingFeedback && (
                  <button
                    onClick={() => setEditingFeedback(false)}
                    style={{
                      padding: "8px 16px", borderRadius: 6, background: "transparent",
                      border: `1px solid ${t.border}`, color: t.textMuted,
                      fontSize: 12, fontWeight: 600, fontFamily: font.body, cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (feedback.quote.trim().length < 10) return;
                    setFeedbackError("");
                    try {
                      await submitTestimonial({ quote: feedback.quote.trim(), rating: feedback.rating });
                      setFeedbackStatus("submitted");
                      setEditingFeedback(false);
                    } catch (err) {
                      setFeedbackError(err.message || "Failed to submit feedback");
                    }
                  }}
                  style={{
                    padding: "8px 20px", borderRadius: 6, border: "none",
                    background: t.navy || "#0F2B3C", color: "#fff",
                    fontSize: 12, fontWeight: 600, fontFamily: font.body,
                    cursor: feedback.quote.trim().length >= 10 ? "pointer" : "not-allowed",
                    opacity: feedback.quote.trim().length >= 10 ? 1 : 0.5,
                  }}
                >
                  {editingFeedback || feedbackStatus === "existing" ? "Update" : "Submit"}
                </button>
              </div>
              <Alert message={feedbackError} onDismiss={() => setFeedbackError("")} style={{ marginTop: 10 }} />
            </>
          )}
        </Panel>
      )}
    </div>
  );
}
